import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { registerUser, loginUser } from "../../lib/auth/client";

interface AuthFormProps {
  mode: "login" | "register";
}

export function AuthForm({ mode }: AuthFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = mode === "login"
        ? await loginUser(email, password)
        : await registerUser(email, password, name);

      if (!result.success) {
        throw new Error(result.message);
      }
      navigate({ to: "/dashboard" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            {mode === "login"
              ? t("auth.login", "Login")
              : t("auth.register", "Register")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">{t("auth.name", "Name")}</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder={t("auth.namePlaceholder", "Enter your name")}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email", "Email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t("auth.emailPlaceholder", "Enter your email")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password", "Password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder={t("auth.passwordPlaceholder", "Enter your password")}
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading
                ? t("common.processing", "Processing...")
                : mode === "login"
                  ? t("auth.login", "Login")
                  : t("auth.register", "Register")}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            {mode === "login" ? (
              <span>
                {t("auth.noAccount", "Don't have an account?")}{" "}
                <button
                  onClick={() => navigate({ to: "/register" })}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t("auth.register", "Register")}
                </button>
              </span>
            ) : (
              <span>
                {t("auth.haveAccount", "Already have an account?")}{" "}
                <button
                  onClick={() => navigate({ to: "/login" })}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t("auth.login", "Login")}
                </button>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
